import {
  Fragment,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  ViewerFragmentCommonProps,
  ViewerFragmentHandle,
} from "../ViewerFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import TitledTextArea from "@coworker/apprestructured/src/task-management/components/TitledTextArea/TitledTextArea";
import styles from "./FailedCriteriaViewerFragment.module.css";
import { useTranslation } from "react-i18next";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUser } from "@coworker/apprestructured/src/shared/hooks/users/useUser";
import { getSchemaForTaskPath } from "@coworker/apprestructured/src/shared/helpers/task.helper";
import ModalColumn from "../../../ModalColumn/ModalColumn";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import { Task } from "@coworker/apprestructured/src/task-management/types/Task";
import type {
  FailedCriteriaItem,
  FailedCriteriaComment,
} from "@coworker/apprestructured/src/task-management/types/TestBuy";

export type FailedCriteriaViewerFragmentConfigProps = {
  field: string;
};

type FailedCriteriaViewerFragmentProps = {
  configProps: FailedCriteriaViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
  ref?: Ref<ViewerFragmentHandle>;
};

type FailedCriteriaItemEntry = {
  item: FailedCriteriaItem;
  value: string;
  error: string | null;
};

// Only the initial comment (from the create form) is shown as a card; the
// second comment is always the one being edited live in the textarea below.
function getCardComments(
  taskState: Task["state"],
  item: FailedCriteriaItem
): FailedCriteriaComment[] {
  if (taskState === "pending")
    return (item.comments || []).filter((comment) => comment.initial_comment);

  return item.comments || [];
}

function FailedCriteriaItem({
  taskState,
  entry,
  onChangeComment,
  onChangeCompleted,
  disabled,
  showValidation,
}: {
  taskState: Task["state"];
  entry: FailedCriteriaItemEntry;
  onChangeComment: (newValue: string, entry: FailedCriteriaItemEntry) => void;
  onChangeCompleted: (
    completed: boolean,
    entry: FailedCriteriaItemEntry
  ) => void;
  disabled: boolean;
  showValidation: boolean;
}) {
  const { t } = useTranslation();
  const { item, value, error } = entry;

  const headerClassName = `${styles["criteria-item-header"]} ${item.completed ? styles["criteria-item-header--completed"] : ""}`;
  const comments = getCardComments(taskState, item);
  const validationError =
    showValidation && !item.completed
      ? t("completeCriteriaToFinishString")
      : null;

  return (
    <ModalColumn gap="medium">
      <FixaListView
        id={`failed-criteria-${item.type}`}
        className={styles["criteria-item"]}
      >
        <div className={headerClassName}>
          <FixaCheckbox
            id={`failed-criteria-${item.type}-checkbox`}
            value={item.type}
            checked={item.completed}
            onChange={() => onChangeCompleted(!item.completed, entry)}
            subtle
            disabled={disabled}
          />

          <FixaText textStyle="Body.M">
            {t(`tb${item.type}CheckString`)}
          </FixaText>
        </div>

        {validationError && (
          <FixaText textStyle="Body.S" className={styles["validation-error"]}>
            {validationError}
          </FixaText>
        )}

        {comments?.map((comment, index) => (
          <ModalColumn
            key={index}
            gap="small"
            className={styles["criteria-item-comment"]}
          >
            <FixaText textStyle="Body.M">
              <b>{t("noteFromString", { name: comment.title })}</b>
            </FixaText>
            <FixaText textStyle="Caption.S">
              <i>{comment.text}</i>
            </FixaText>
          </ModalColumn>
        ))}

        <TitledTextArea
          className={styles["criteria-item-description"]}
          label={t("addNoteString")}
          value={value}
          onChange={(newValue) => onChangeComment(newValue, entry)}
          disabled={disabled}
          error={error}
        />
      </FixaListView>
    </ModalColumn>
  );
}

function FailedCriteriaViewerFragment({
  configProps,
  commonProps,
  ref,
}: FailedCriteriaViewerFragmentProps) {
  const { t } = useTranslation();
  const myUserId = useUID();
  const myFullName = useUser(myUserId).data?.fullName;
  const { task, taskType, onTaskChange, blockInput } = commonProps;
  let { showValidation } = commonProps;
  const { field } = configProps;
  const taskState = getByPath(task, "state") as Task["state"];
  const wrapperClassName = `${styles["wrapper"]} ${taskState !== "pending" ? styles["wrapper--completed"] : ""}`;

  const maxCommentLength =
    getSchemaForTaskPath(taskType, `${field}.comments.text`)?.maxLength || 1000;

  const criteriaDataValue = getByPath(task, field);
  const criteriaData = isLoading(criteriaDataValue)
    ? undefined
    : (criteriaDataValue as FailedCriteriaItem[] | undefined);

  const criteriaValues = useMemo(() => {
    return criteriaData?.map((item) => {
      const editableComment = item.comments?.find(
        (comment) => !comment.initial_comment
      );
      const value = editableComment?.text ?? "";
      const error =
        value.length > maxCommentLength
          ? t("maximumCharactersLengthString", { count: maxCommentLength })
          : null;
      return { item, value, error };
    });
  }, [criteriaData, maxCommentLength, t]);

  const isLoadingValues = criteriaValues?.some((item) => isLoading(item.value));

  const handleChangeCompletedEntry = useCallback(
    (completed: boolean, entry: FailedCriteriaItemEntry) => {
      const updatedCriteria = (criteriaData ?? []).map((criteria) =>
        criteria.type === entry.item.type
          ? { ...criteria, completed }
          : criteria
      );

      onTaskChange(setByPath(field, updatedCriteria));
    },
    [criteriaData, field, onTaskChange]
  );

  const handleChangeCommentEntry = useCallback(
    (newValue: string, entry: FailedCriteriaItemEntry) => {
      if (
        newValue.length > maxCommentLength &&
        newValue.length >= entry.value.length
      ) {
        return;
      }

      const initialComment = entry.item.comments?.find(
        (comment) => comment.initial_comment
      );

      const updatedComments = initialComment ? [initialComment] : [];

      if (newValue.trim().length > 0) {
        updatedComments.push({
          initial_comment: false,
          creator_user_id: myUserId,
          title: myFullName ?? "",
          text: newValue,
          created_at: new Date().toISOString(),
        });
      }

      const updatedCriteria = (criteriaData ?? []).map((criteria) =>
        criteria.type === entry.item.type
          ? { ...criteria, comments: updatedComments }
          : criteria
      );

      onTaskChange(setByPath(field, updatedCriteria));
    },
    [criteriaData, field, myUserId, myFullName, onTaskChange, maxCommentLength]
  );

  // Right now, most of the tasks are able to be completed without running validations.
  // As this feature is not fully implemented yet but requested by coworkers, we will not block the user
  // from completing the task if the criteria are not completed.
  showValidation = false;
  const fragmentValid = true; /*useMemo(
    () => !criteriaData || criteriaData.every((item) => item.completed),
    [criteriaData]
  )()*/

  // Expose validation state to parent via ref
  useImperativeHandle(ref, () => {
    return { valid: fragmentValid };
  }, [fragmentValid]);

  if (isLoadingValues) {
    return <LoadingCreatorFragment />;
  }

  if (!criteriaValues || criteriaValues.length === 0) {
    return null;
  }

  return (
    <div className={wrapperClassName}>
      <FixaText textStyle="Heading.S">{t("checklistString")}</FixaText>

      {criteriaValues.map((entry) => (
        <Fragment key={entry.item.type}>
          <FailedCriteriaItem
            taskState={taskState}
            entry={entry}
            onChangeComment={handleChangeCommentEntry}
            onChangeCompleted={handleChangeCompletedEntry}
            disabled={blockInput}
            showValidation={showValidation}
          />
        </Fragment>
      ))}
    </div>
  );
}

export default FailedCriteriaViewerFragment;
